import { useContext } from 'react';
import { WalletContext } from '../context/WalletContext';

export const useWallet = () => {
  const context = useContext(WalletContext);
  
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }

  // Helper computed properties
  const isLoading = context.status === 'connecting' || context.authStatus === 'checking' || context.authStatus === 'authenticating';
  const hasError = !!context.error || !!context.authError;
  const canAuthenticate = context.isConnected && !context.isAuthenticated && context.authStatus !== 'authenticating';
  const needsAuthentication = context.isConnected && !context.isAuthenticated;
  
  // Session status helpers
  const sessionExpiresAt = context.sessionExpiresAt ? new Date(context.sessionExpiresAt) : null;
  const sessionExpired = sessionExpiresAt ? new Date() > sessionExpiresAt : false;
  const sessionExpiresIn = sessionExpiresAt ? Math.max(0, sessionExpiresAt.getTime() - Date.now()) : 0;
  const sessionExpiresInHours = Math.floor(sessionExpiresIn / (1000 * 60 * 60));
  const sessionExpiresInDays = Math.floor(sessionExpiresInHours / 24);

  // Helper functions for common patterns
  const connectAndAuthenticate = async () => {
    try {
      if (!context.isConnected) {
        await context.connect();
      }
      if (!context.isAuthenticated) {
        await context.authenticateWallet();
      }
    } catch (error) {
      console.error('Connect and authenticate failed:', error);
      throw error;
    }
  };

  const ensureAuthenticated = async () => {
    if (!context.isConnected) {
      throw new Error('Wallet not connected');
    }
    if (!context.isAuthenticated) {
      await context.authenticateWallet();
    }
  };

  return {
    // Original context properties
    ...context,
    
    // Helper computed properties
    isLoading,
    hasError,
    canAuthenticate,
    needsAuthentication,
    
    // Session helpers
    sessionExpiresAt,
    sessionExpired,
    sessionExpiresIn,
    sessionExpiresInHours,
    sessionExpiresInDays,
    
    // Helper functions
    connectAndAuthenticate,
    ensureAuthenticated,
  };
}; 